import { Component, OnInit, ElementRef, ViewChild, Output, EventEmitter } from '@angular/core';
import { DiscoveryChatQuestion, PossibleResponse, ChatConsultant, ChatRollMessage, ChatUser, ChatUserType } from 'src/app/discovery-process/model/discovery-chat.interface';
import { sortBy, floor } from 'lodash';
import { DoneDiscoveryProcessComponent } from 'src/app/discovery-process/done-discovery-process/done-discovery-process.component';
import { BaseComponent } from 'src/app/moov-common/base/base.component';
import { AbstractDiscoveryProcessService } from 'src/app/discovery-process/discovery-process.abstract.service';
import { AbstractAuthorizationService } from 'src/app/moov-common/services/authorization/authorization.abstract.service';
import { AbstractPersonalUserDataService } from 'src/app/moov-common/services/personal-user-data/personal-user-data.abstract.service';
import { firstValueFrom } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';

@Component({
    selector: 'app-embedded-discovery-chat',
    templateUrl: './embedded-discovery-chat.component.html',
    styleUrls: ['./embedded-discovery-chat.component.scss'],
})
export class EmbeddedDiscoveryChatComponent extends BaseComponent implements OnInit {
    @ViewChild('chatBody') chatBodyRef: ElementRef<HTMLDivElement>;
    @Output() discoveryCompleted = new EventEmitter<void>();

    isDiscoveryAlreadyDone: boolean = false;
    questionList: DiscoveryChatQuestion[] = [];
    currentQuestionPos: number = 0;
    consultant: ChatConsultant = { id: "C001", name: "אביבית", title: "יועצת קריירה", photoURL: 'https://storage.googleapis.com/media.landbot.io/152285/channels/G3Y2NEP25EGJGBKL8J81MHWEMN1EGDB5.png', type: ChatUserType.Consultant };
    chatUser: ChatUser;
    answeredQuestionList: ChatRollMessage[] = [];
    isLoading: boolean = false;
    currentQuestionSetId: string = "";
    lastQuestionSetId: string = "";
    prevQuestionSetResponseToRemove: PossibleResponse[] = [];
    historicResponses: Map<string, PossibleResponse>;
    progressBarValue = 0;
    focusBottom = false;
    TOTAL_QUESTIONS_NUMBER = 66;

    constructor(
        private readonly discoveryProcessService: AbstractDiscoveryProcessService,
        public dialog: MatDialog,
        private readonly authService: AbstractAuthorizationService,
        private readonly personalUserDataService: AbstractPersonalUserDataService,
    ) {
        super();
        this.historicResponses = new Map<string, PossibleResponse>();
    }

    ngOnInit(): void {
        super.ngOnInit();
        if (this.user) {
            this.chatUser = {
                id: this.user.id,
                name: this.user.firstName + " " + this.user.lastName,
                photoURL: this.user.image ? this.user.image : "",
                type: ChatUserType.User,
                title: "",
            };

            try {
                this.discoveryProcessService.continueUserJourney().subscribe(response => {
                    if (!response?.journeyId) {
                        // journeyId is null — discovery is already finished
                        this.isDiscoveryAlreadyDone = true;
                        this.discoveryCompleted.emit();
                        return;
                    }
                    this.TOTAL_QUESTIONS_NUMBER = response.totalQuestions || 66;
                    this.discoveryProcessService.journeyGetQuestionsInBatch().subscribe(batchResponse => {
                        if (batchResponse && batchResponse.length > 0) {
                            this.questionList = sortBy(batchResponse, ['batchIdx']);
                            this.currentQuestionSetId = this.questionList[this.currentQuestionPos].setId;
                            this.setProgressBarValue();
                        } else {
                            console.log('embedded-discovery-chat: no questions returned, discovery already done');
                            this.isDiscoveryAlreadyDone = true;
                            this.discoveryCompleted.emit();
                        }
                    });
                });
            } catch (e) {
                console.log('embedded-discovery-chat ngOnInit error in journeyGetQuestionsInBatch or continueUserJourney');
                window.location.reload();
            }
        }
    }

    setQuestionResponse(currentQuestion: DiscoveryChatQuestion) {
        this.showLoading();
        this.focusBottom = false;

        let selectedResponses: PossibleResponse[] = [];
        this.prevQuestionSetResponseToRemove = [];
        currentQuestion.possibleResponses.forEach(res => {
            if ((res.selected) && (res.questionId == currentQuestion.id)) {
                selectedResponses.push(res);
                this.prevQuestionSetResponseToRemove.push(res);
                this.historicResponses.set(currentQuestion.id, res);
            }
        });

        if (selectedResponses !== undefined) {
            let selectedResponseIds: string[] = selectedResponses.map(({ id }) => id);

            try {
                this.discoveryProcessService.journeySetQuestionResponse(currentQuestion.id, selectedResponseIds).subscribe(response => {
                    this.addQuestionToChatRoll(currentQuestion, selectedResponses);
                    this.showNextQuestion();
                });
            } catch (e) {
                console.log('embedded-discovery-chat setQuestionResponse error in journeySetQuestionResponse');
                window.location.reload();
            }
        }

        this.setProgressBarValue();
    }

    setProgressBarValue() {
        const rawValue = floor(this.questionList[this.currentQuestionPos].idx / this.TOTAL_QUESTIONS_NUMBER * 100);
        this.progressBarValue = rawValue;
    }

    selectDiffernetAnswer() {
        this.showLoading();
        this.currentQuestionPos--;
        this.questionList[this.currentQuestionPos].isAnswered = false;

        if (this.currentQuestionPos == 0) {
            this.lastQuestionSetId = "";
        } else {
            this.lastQuestionSetId = this.questionList[this.currentQuestionPos - 1].setId;
        }

        this.questionList[this.currentQuestionPos].possibleResponses.forEach((element: PossibleResponse) => {
            element.selected = false;
        });

        this.currentQuestionSetId = this.questionList[this.currentQuestionPos].setId;

        this.prevQuestionSetResponseToRemove = [];
        if (this.lastQuestionSetId == this.currentQuestionSetId) {
            let response = this.historicResponses.get(this.questionList[this.currentQuestionPos].id);
            if (response != undefined) {
                this.prevQuestionSetResponseToRemove.push(response);
            }
        }

        this.answeredQuestionList.pop();
        this.answeredQuestionList.pop();

        this.hideLoading();
    }

    private addQuestionToChatRoll(currentQuestion: DiscoveryChatQuestion, selectedResponses?: PossibleResponse[]) {
        if (selectedResponses) {
            let displayedConsultantQuestion = false;
            selectedResponses.forEach(selectedResponse => {
                let hour: number = new Date().getHours();
                let minutes: number = new Date().getMinutes();
                let timestamp: string = `${hour}:${minutes}`;

                if (!displayedConsultantQuestion) {
                    let chatRollMessageConsultant: ChatRollMessage = {
                        id: currentQuestion.id, participant: this.consultant, text: currentQuestion.questionText, timestamp
                    };
                    this.answeredQuestionList.push(chatRollMessageConsultant);
                    displayedConsultantQuestion = true;
                }

                let chatRollMessageUser: ChatRollMessage = {
                    id: currentQuestion.id, participant: this.chatUser, text: selectedResponse.responseText, timestamp
                };

                this.answeredQuestionList.push(chatRollMessageUser);
                this.answeredQuestionList = ([] as ChatRollMessage[]).concat(this.answeredQuestionList);
            });

            this.focusBottom = true;
        }
    }

    private async showNextQuestion() {
        this.questionList[this.currentQuestionPos].isAnswered = true;
        if (this.currentQuestionPos < this.questionList.length - 1) {
            this.currentQuestionPos++;
            this.hideLoading();
        } else {
            await this.getNextBatchOrDone();
            this.prevQuestionSetResponseToRemove = [];
        }
        this.lastQuestionSetId = this.currentQuestionSetId;
        this.currentQuestionSetId = this.questionList[this.currentQuestionPos].setId;
        if (this.lastQuestionSetId !== this.currentQuestionSetId) {
            this.prevQuestionSetResponseToRemove = [];
        }
    }

    private async getNextBatchOrDone() {
        let batchResponse: any;

        try {
            batchResponse = await firstValueFrom(this.discoveryProcessService.journeyGetQuestionsInBatch());
        } catch (e) {
            console.log('embedded-discovery-chat getNextBatchOrDone error in journeyGetQuestionsInBatch');
            window.location.reload();
        }

        if (batchResponse && batchResponse.length > 0) {
            batchResponse = sortBy(batchResponse, ['batchIdx']);
            this.questionList = this.questionList.concat(batchResponse);
            this.currentQuestionPos++;
            this.hideLoading();
        } else {
            this.showDone();
        }
    }

    private showDone() {
        this.analytics.discoveryProcessCompletedEvent();

        this.authService.updateLoggedInUserToken().subscribe(res => {
            const dialogRef = this.dialog.open(DoneDiscoveryProcessComponent, {
                height: '40%',
                width: '60%',
                data: { messageKeyPrefix: 'discoveryProcess.discoveryDoneMessageStructured' }
            });

            dialogRef.afterClosed().subscribe(res => {
                // Instead of routing, emit completion event
                this.discoveryCompleted.emit();
            });
        });
    }

    private showLoading() {
        this.isLoading = true;
    }

    private hideLoading() {
        this.isLoading = false;
    }

    scrollToBottom() {
        this.chatBodyRef.nativeElement.scrollTop = this.chatBodyRef.nativeElement.scrollHeight;
    }
}
